import { useEffect, useState } from "react";
import api, { fileUrl } from "../../lib/api";
import { Plus, Trash2, Save, X, Upload as UploadIcon, Pencil, Copy } from "lucide-react";

const empty = { title: "", slug: "", summary: "", overview: "", interventions: [], impacts: [], icon: "Leaf", image_url: "", order: 0, published: true };

const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80);

export default function ProgramsEditor() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);

    const load = () => api.get("/programs?all=true").then(({ data }) => setItems(data));
    useEffect(() => { load(); }, []);

    const save = async (item) => {
        const payload = { ...item, interventions: item.interventions || [], impacts: item.impacts || [], order: parseInt(item.order) || 0 };
        if (item.id) await api.put(`/programs/${item.id}`, payload);
        else await api.post("/programs", payload);
        setEditing(null);
        load();
    };
    const del = async (id) => {
        if (!window.confirm("Delete this program?")) return;
        await api.delete(`/programs/${id}`);
        load();
    };
    const dup = async (id) => {
        await api.post(`/programs/${id}/duplicate`);
        load();
    };

    return (
        <div data-testid="programs-editor">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="font-display text-4xl mb-2">Programs</h1>
                    <p className="text-muted-foreground" data-testid="programs-count">Manage your {items.length} thematic {items.length === 1 ? "area" : "areas"}.</p>
                </div>
                <button onClick={() => setEditing({ ...empty })} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium" data-testid="add-program">
                    <Plus size={14} /> New Program
                </button>
            </div>

            <div className="bg-card border border-border rounded-sm overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-muted/50 text-xs uppercase tracking-[0.18em] text-muted-foreground">
                        <tr>
                            <th className="text-left p-4">#</th>
                            <th className="text-left p-4">Title</th>
                            <th className="text-left p-4">Slug</th>
                            <th className="text-left p-4">Published</th>
                            <th className="text-right p-4">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((p) => (
                            <tr key={p.id} className="border-t border-border" data-testid={`program-row-${p.slug}`}>
                                <td className="p-4 text-muted-foreground">{p.order}</td>
                                <td className="p-4 font-medium">{p.title}</td>
                                <td className="p-4 text-muted-foreground font-mono text-xs">{p.slug}</td>
                                <td className="p-4">{p.published ? <span className="text-secondary">● Yes</span> : <span className="text-muted-foreground">○ No</span>}</td>
                                <td className="p-4 text-right space-x-2">
                                    <button onClick={() => setEditing({ ...p })} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm border border-border hover:border-primary/60 text-xs"><Pencil size={12} /> Edit</button>
                                    <button onClick={() => dup(p.id)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm border border-border text-xs" data-testid={`dup-program-${p.slug}`}><Copy size={12} /> Duplicate</button>
                                    <button onClick={() => del(p.id)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm border border-destructive text-destructive hover:bg-destructive/10 text-xs"><Trash2 size={12} /> Delete</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {editing && (
                <ProgramModal item={editing} onSave={save} onClose={() => setEditing(null)} onChange={setEditing} />
            )}
        </div>
    );
}

const ProgramModal = ({ item, onSave, onClose, onChange }) => {
    const set = (k, v) => onChange({ ...item, [k]: v });
    const uploadImage = async (file) => {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        set("image_url", data.url);
    };
    const setList = (k, v) => set(k, v.split("\n").map((s) => s.trim()).filter(Boolean));

    return (
        <div className="fixed inset-0 z-50 bg-foreground/50 flex items-start justify-center p-6 overflow-y-auto" data-testid="program-modal">
            <div className="bg-card border border-border rounded-sm w-full max-w-3xl my-10">
                <div className="flex items-center justify-between p-6 border-b border-border">
                    <h2 className="font-display text-2xl">{item.id ? "Edit Program" : "New Program"}</h2>
                    <button onClick={onClose} className="p-2 rounded-sm hover:bg-muted"><X size={18} /></button>
                </div>
                <div className="p-6 space-y-5">
                    <Field label="Title" value={item.title} onChange={(v) => onChange({ ...item, title: v, slug: item.id ? item.slug : slugify(v) })} testid="prog-title" />
                    <Field label="Slug (URL)" value={item.slug} onChange={(v) => set("slug", slugify(v))} testid="prog-slug" />
                    <Area label="Summary (short)" value={item.summary} onChange={(v) => set("summary", v)} rows={2} testid="prog-summary" />
                    <Area label="Overview (long)" value={item.overview} onChange={(v) => set("overview", v)} rows={5} testid="prog-overview" />
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Icon (lucide name)" value={item.icon} onChange={(v) => set("icon", v)} testid="prog-icon" />
                        <Field label="Order" type="number" value={item.order} onChange={(v) => set("order", v)} testid="prog-order" />
                    </div>
                    <div>
                        <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">Image</span>
                        <div className="flex items-center gap-3">
                            <input value={item.image_url || ""} onChange={(e) => set("image_url", e.target.value)} className="flex-1 px-3 py-2.5 rounded-sm border border-border bg-background text-sm" data-testid="prog-image-url" />
                            <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-sm border border-border cursor-pointer hover:border-primary/60 text-sm"><UploadIcon size={14} /> Upload
                                <input type="file" className="hidden" accept="image/*" onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0])} data-testid="prog-image-upload" />
                            </label>
                        </div>
                        {item.image_url && <img src={fileUrl(item.image_url)} alt="" className="mt-3 h-32 rounded-sm border border-border object-cover" />}
                    </div>
                    <Area label="Key interventions (one per line)" value={(item.interventions || []).join("\n")} onChange={(v) => setList("interventions", v)} rows={6} testid="prog-interventions" />
                    <Area label="Anticipated impacts (one per line)" value={(item.impacts || []).join("\n")} onChange={(v) => setList("impacts", v)} rows={4} testid="prog-impacts" />
                    <label className="inline-flex items-center gap-2">
                        <input type="checkbox" checked={!!item.published} onChange={(e) => set("published", e.target.checked)} data-testid="prog-published" />
                        <span className="text-sm">Published</span>
                    </label>
                </div>
                <div className="p-6 border-t border-border flex justify-end gap-3">
                    <button onClick={onClose} className="px-5 py-2.5 rounded-sm border border-border text-sm">Cancel</button>
                    <button onClick={() => onSave(item)} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium" data-testid="prog-save">
                        <Save size={14} /> Save
                    </button>
                </div>
            </div>
        </div>
    );
};

const Field = ({ label, value, onChange, testid, type = "text" }) => (
    <label className="block">
        <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">{label}</span>
        <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
            className="w-full px-3 py-2.5 rounded-sm border border-border bg-background text-sm" />
    </label>
);
const Area = ({ label, value, onChange, rows = 3, testid }) => (
    <label className="block">
        <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">{label}</span>
        <textarea rows={rows} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
            className="w-full px-3 py-2.5 rounded-sm border border-border bg-background text-sm resize-y" />
    </label>
);
